// src/pages/Production.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Input,
  Spin,
  message,
  Button,
  Tooltip,
  Select,
  Modal,
  Dropdown,
} from "antd";
import {
  DownloadOutlined,
  PrinterOutlined,
  DeleteOutlined,
  SearchOutlined,
  FilterOutlined,
  FileTextOutlined,
  ReloadOutlined,
  SendOutlined,
  MessageOutlined,
  EyeOutlined,
} from "@ant-design/icons";

import axiosInstance from "../utils/axiosInstance";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import JsBarcode from "jsbarcode";

const { Option } = Select;
const { confirm } = Modal;
const { TextArea } = Input;

/** Canonical sets */
const ALL_STORAGE_TYPES = ["Ottoman", "Non Ottoman", "Cloud Storage"];
const ALL_PRIORITIES = ["normal", "urgent"];
const ALL_PROD_STATUSES = ["in progress", "complete", "shipped"];
const LOADING_COLORS = [
  "#16a34a", // loading 1 -> green
  "#f59e0b", // loading 2 -> amber / yellow
  "#ea580c", // loading 3 -> orange
  "#7c3aed", // loading 4 -> purple
  "#64748b", // loading 5 -> gray
];

const hashString = (str) => {
  // djb2-ish hash, stable and fast
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = (h * 33) ^ str.charCodeAt(i);
  }
  return Math.abs(h >>> 0);
};

// Format stored sequence into "MON-MM-SS" e.g. "JAN-01-01"
const formatMonthSequence = (sequence_month, sequence_no) => {
  if (sequence_no == null) return null;
  return String(sequence_no);
};

/** Helper: get company name (kept for future use) */
const getCompanyName = (rec) =>
  rec?.company_name ||
  rec?.customer_company ||
  rec?.customerCompany ||
  rec?.user_company ||
  rec?.user?.company_name ||
  rec?.user?.companyName ||
  rec?.customer?.company_name ||
  rec?.customer?.companyName ||
  "-";

/** Compute package count per order line with safe fallbacks */
const computePackageCount = (rec) => {
  if (rec && typeof rec.packageCount === "number") return rec.packageCount;
  const st = (rec?.storageType || "").toLowerCase();
  const isMeraki = !!rec?.isMerakiDivanOttoman;
  if (st === "ottoman") return isMeraki ? 3 : 5;
  if (st === "non ottoman") return 4;
  if (st === "cloud storage") return 5;
  return null;
};

const generateBarcodeBase64 = (value) => {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, value || " ", {
    format: "CODE128",
    displayValue: false,
    width: 2,
    height: 56,
    margin: 0,
    background: "#ffffff",
    lineColor: "#000000",
  });
  return canvas.toDataURL("image/png");
};

const generateLabelPDF = (record, action = "download") => {
  const margin = 40;
  const labelWidth = 300;
  const labelHeight = 420;
  const pageWidth = labelWidth + margin * 2;
  const pageHeight = labelHeight + margin * 2;

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: [pageWidth, pageHeight],
    compress: true,
  });

  doc.setDrawColor(0);
  doc.setLineWidth(1.2);
  doc.rect(margin, margin, labelWidth, labelHeight, "S");

  let rows = [50, 70, 160, 90, 70, 40, 90];
  const sum = rows.reduce((a, b) => a + b, 0);
  if (sum !== labelHeight) {
    const k = labelHeight / sum;
    rows = rows.map((r) => Math.round(r * k));
  }

  let y = margin;
  for (let i = 0; i < rows.length - 1; i++) {
    y += rows[i];
    doc.setLineWidth(1);
    doc.line(margin, y, margin + labelWidth, y);
  }

  const centerX = margin + labelWidth / 2;

  const drawCentered = (text, rowIndex, { size = 14, bold = false } = {}) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(0);
    const top = margin + rows.slice(0, rowIndex).reduce((a, b) => a + b, 0);
    const height = rows[rowIndex];
    const baseline = top + height / 2 + size * 0.35;
    doc.text(text || "", centerX, baseline, { align: "center" });
  };

  const sizeText = (record.size || "").toString().toUpperCase();

  const fabricNameRaw = record.fabric || record.fabricName || record.fabric_name || "";
  const fabricColorRaw =
    record.fabricColor || record.color || record.fabric_color || record.color_name || "";
  const fabricCombined = `${(fabricColorRaw || "").toString().trim()} ${(fabricNameRaw || "")
    .toString()
    .trim()}`.trim();
  const fabricText = fabricCombined ? fabricCombined.toUpperCase() : "";

  const productText = (record.product_name || "").toString().toUpperCase();
  const storageText = (record.storageType || "").toString().toUpperCase();
  const refText = ((record.referenceNumber ?? "") + "").toUpperCase() || "—";

  const pkgCount = computePackageCount(record);
  const totalPackages = pkgCount != null ? String(pkgCount) : "";

const seqLabel = formatMonthSequence(record.sequence_month, record.sequence_no);
  if (seqLabel) {
    const badgeRadius = 12; // 🔹 smaller circle
    const rightPadding = 12; // distance from right border

    // FIRST ROW (row index 0)
    const rowTop = margin;
    const rowHeight = rows[0];
    // center vertically inside first row
    const badgeCy = rowTop + rowHeight / 2;

    // right side, inside label border
    const badgeCx = margin + labelWidth - badgeRadius - rightPadding;

    // circle
    doc.setDrawColor(0);
    doc.setLineWidth(1.2);
    doc.setFillColor(255, 255, 255);
    doc.circle(badgeCx, badgeCy, badgeRadius, "FD");

    // text inside circle
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0);

    // optical vertical centering
    doc.text(String(seqLabel), badgeCx, badgeCy + 3, { align: "center" });
  }

  drawCentered(sizeText, 0, { size: 22, bold: true });

  {
    const top = margin + rows[0];
    const height = rows[1];
    const maxW = labelWidth - 30;

    let fs = 20;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(fs);

    const toDraw = fabricText || "";
    let lines = doc.splitTextToSize(toDraw, maxW);
    while (lines.length > 2 && fs > 14) {
      fs -= 1;
      doc.setFontSize(fs);
      lines = doc.splitTextToSize(toDraw, maxW);
    }

    const lineH = fs * 1.15;
    const totalH = lines.length * lineH;
    let ly = top + (height - totalH) / 2 + lineH - 2;

    lines.forEach((ln) => {
      doc.text(ln, centerX, ly, { align: "center" });
      ly += lineH;
    });
  }

  {
    const top = margin + rows.slice(0, 2).reduce((a, b) => a + b, 0);
    const height = rows[2];
    const maxW = labelWidth - 30;

    let fs = 26;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(fs);

    let lines = doc.splitTextToSize(productText, maxW);
    while (lines.length > 3 && fs > 16) {
      fs -= 2;
      doc.setFontSize(fs);
      lines = doc.splitTextToSize(productText, maxW);
    }

    const lineH = fs * 1.15;
    const totalH = lines.length * lineH;
    let ly = top + (height - totalH) / 2 + lineH - 2;

    lines.forEach((ln) => {
      doc.text(ln, centerX, ly, { align: "center" });
    });
  }

  if (storageText) {
    drawCentered(storageText, 3, { size: 18, bold: true });
  }

  {
    const top = margin + rows.slice(0, 4).reduce((a, b) => a + b, 0);
    const height = rows[4];

    const headSize = 14;
    const valSize = 18;
    const headLH = headSize * 1.1;
    const valLH = valSize * 1.1;
    const gap = 6;
    const totalH = headLH + gap + valLH;

    let by = top + (height - totalH) / 2 + headSize;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(headSize);
    doc.text("CUSTOMER REF:", centerX, by, { align: "center" });

    by += headLH + gap;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(valSize);
    doc.text(refText, centerX, by, { align: "center" });
  }

  drawCentered(`TOTAL PACKAGES: ${totalPackages}`, 5, { size: 20, bold: true });

  {
    const code = record.BarcodeData ? String(record.BarcodeData) : "123456";
    const img = generateBarcodeBase64(code);

    const top = margin + rows.slice(0, 6).reduce((a, b) => a + b, 0);
    const height = rows[6];

    const innerPadX = 20;
    const innerPadY = 12;
    const bw = labelWidth - innerPadX * 2;
    const bh = Math.max(36, Math.min(56, height - innerPadY * 2));

    const bx = centerX - bw / 2;
    const by = top + (height - bh) / 2;

    doc.setFillColor(255, 255, 255);
    doc.rect(bx - 4, by - 4, bw + 8, bh + 8, "F");

    doc.addImage(img, "PNG", bx, by, bw, bh);
  }

  const clean =
    (record.product_name || "label").toString().replace(/[^a-z0-9]/gi, "_").substring(0, 20) ||
    "label";

  if (action === "print") {
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const printWindow = window.open(pdfUrl);
    if (printWindow) {
      printWindow.onload = function () {
        printWindow.print();
        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 1000);
      };
    }
  } else {
    doc.save(`Label_${record.trackingId || "0000"}_${clean}.pdf`);
  }
};

// ===== Special Quote parser (same as Orders.jsx) =====
const safeJsonParse = (v) => {
  if (v == null) return null;
  if (typeof v === "object") return v;
  if (typeof v !== "string") return null;
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};

const resolveUrl = (u) => {
  const url = String(u || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;

  const base = (axiosInstance?.defaults?.baseURL || "").trim();
  if (!base) return url;

  const b = base.replace(/\/$/, "");
  const p = url.startsWith("/") ? url : `/${url}`;
  return `${b}${p}`;
};

const fileNameFromUrl = (url) => {
  try {
    const clean = String(url || "").split("?")[0].split("#")[0];
    const parts = clean.split("/");
    return decodeURIComponent(parts[parts.length - 1] || "file");
  } catch {
    return "file";
  }
};

const isPdfUrl = (url) => /\.pdf(\?|#|$)/i.test(String(url || ""));
const isImageUrl = (url) =>
  /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(String(url || ""));

const normalizeSpecialQuote = (order) => {
  const raw =
    order?.special_quote ||
    order?.specialQuote ||
    order?.specialInstruction ||
    order?.specialInstructions ||
    order?.special_quote_json ||
    null;

  const obj = safeJsonParse(raw);

  const text =
    (obj && (obj.text || obj.note || obj.message)) ||
    (typeof raw === "string" && !obj ? raw : "") ||
    "";

  const filesRaw =
    (obj && (obj.files || obj.attachments || obj.uploads)) ||
    order?.files ||
    order?.attachments ||
    [];

  const filesArr = Array.isArray(filesRaw) ? filesRaw : [];

  const files = filesArr
    .map((f) => {
      if (!f) return null;

      if (typeof f === "string") {
        const url = resolveUrl(f);
        const name = fileNameFromUrl(url);
        return { url, name, isPdf: isPdfUrl(url), isImage: isImageUrl(url) };
      }

      const url =
        resolveUrl(
          f.url || f.fileUrl || f.path || f.file || f.location || f.link || f.src || ""
        ) || "";
      if (!url) return null;

      const name =
        f.name ||
        f.fileName ||
        f.filename ||
        f.originalName ||
        f.original_name ||
        fileNameFromUrl(url);

      const mime = String(f.mimeType || f.mimetype || f.type || "").toLowerCase();
      const isPdf = mime.includes("pdf") || isPdfUrl(url);
      const isImage = mime.startsWith("image/") || isImageUrl(url);

      return { url, name, isPdf, isImage };
    })
    .filter(Boolean);

  const hasAny = Boolean(String(text || "").trim()) || files.length > 0;
  return { text: String(text || ""), files, hasAny };
};

const ProductionOrders = () => {
  const userRole = localStorage.getItem("userRole") || "";
  const loggedInUserId = Number(localStorage.getItem("userId"));
  const loggedInUserName = localStorage.getItem("userName");
  const isSalesUser = userRole === "sales";
  const isProductionAdmin = (userRole || "").toLowerCase().includes("production admin");
  const isProductionManager = (userRole || "").toLowerCase().includes("production manager");
  const [sortOrder, setSortOrder] = useState("newest"); // newest | oldest
  const loadingColorMapRef = useRef(new Map());
// counter to assign next free color index when new comments appear
const nextLoadingColorRef = useRef(0);

  const [orders, setOrders] = useState([]);
  const [notes, setNotes] = useState({});
  const [newStatuses, setNewStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Filters
  const [filterStorage, setFilterStorage] = useState();
  const [filterPriority, setFilterPriority] = useState();
  const [filterProdStatus, setFilterProdStatus] = useState();
  const [filterSize, setFilterSize] = useState();
  const [filterLoading, setFilterLoading] = useState(); // <-- NEW
  // Month / Year filters
  const [availableMonths, setAvailableMonths] = useState([]); // [{ month: '2026-01', count: 10 }, ...]
  const [availableYears, setAvailableYears] = useState({}); // { '2026': 101, ... }
  const [selectedYearFilter, setSelectedYearFilter] = useState();
  const [selectedMonthFilter, setSelectedMonthFilter] = useState();

  const [loadingModalVisible, setLoadingModalVisible] = useState(false);
  const [loadingModalComment, setLoadingModalComment] = useState("");
  const [loadingModalTargetIds, setLoadingModalTargetIds] = useState([]); // order ids being added to loading
  const [loadingCreating, setLoadingCreating] = useState(false);

  // Loading details modal state (NEW)
  const [loadingDetailsVisible, setLoadingDetailsVisible] = useState(false);
  const [loadingDetailsList, setLoadingDetailsList] = useState([]);

  // SIZES: full list from DB
  const [allSizesFromDb, setAllSizesFromDb] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    filterStorage,
    filterPriority,
    filterProdStatus,
    filterSize,
    selectedYearFilter,
    selectedMonthFilter,
    filterLoading, 
    sortOrder,
  ]);

  // ==== CHAT STATE ====
  const [chatVisible, setChatVisible] = useState(false);
  const [chatOrder, setChatOrder] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const chatScrollRef = useRef(null);
  const chatAbortRef = useRef(null);
  // ====================

  // ===== Helpers required for VIP chat =====
  const sameDay = (a, b) => {
    if (!a || !b) return false;
    const da = new Date(a),
      db = new Date(b);
    return (
      da.getFullYear() === db.getFullYear() &&
      da.getMonth() === db.getMonth() &&
      da.getDate() === db.getDate()
    );
  };

  const prettyDay = (d) =>
    d
      ? new Date(d).toLocaleDateString(undefined, {
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "";

  const prettyTime = (d) =>
    d ? new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

  const copyRef = async (ref) => {
    try {
      await navigator.clipboard.writeText(String(ref || ""));
      message.success("Reference copied");
    } catch {
      message.warning("Couldn't copy");
    }
  };

  const onChatKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (chatText.trim()) sendChatMessage();
    }
  };

  const getInitials = (name) => {
    const n = String(name || "").trim();
    if (!n) return "U";
    const parts = n.split(/\s+/);
    const a = parts[0]?.[0] || "";
    const b = parts[1]?.[0] || parts[0]?.[1] || "";
    return (a + b).toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const d = new Date(dateString);
      if (!isNaN(d.getTime())) {
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = String(d.getFullYear());
        return `${dd}/${mm}/${yyyy}`;
      }
      // Fallback: accept "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM:SS" strings
      const [day] = String(dateString).split("T");
      const parts = day.split("-");
      if (parts.length === 3) {
        const [yyyy, mm, dd] = parts;
        return `${String(dd).padStart(2, "0")}/${String(mm).padStart(2, "0")}/${yyyy}`;
      }
      return String(dateString);
    } catch {
      return String(dateString);
    }
  };

  const formatStorageType = (type) => {
    if (!type) return "N/A";
    switch (String(type).toLowerCase()) {
      case "ottoman":
        return "Ottoman";
      case "non ottoman":
        return "Non Ottoman";
      case "cloud storage":
        return "Cloud Storage";
      default:
        return type;
    }
  };
  // Convert "YYYY-MM" -> "Jan 2026"
  const monthLabel = (yyyyMm) => {
    if (!yyyyMm) return "";
    const [, m] = String(yyyyMm).split("-");
    if (!m) return "";
    const date = new Date(2000, Number(m) - 1, 1);
    return date.toLocaleString(undefined, { month: "short" }); // "Jan"
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axiosInstance.get("/admin/production/list", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const list = Array.isArray(data?.orders) ? data.orders : Array.isArray(data) ? data : [];

     const formatted = list.map((o) => {
  const rawSize =
    typeof o.size === "string"
      ? (o.size || "").replace(/\u00a0/g, " ").trim()
      : (o.size?.size || "N/A").replace(/\u00a0/g, " ").trim();

  return {
    id: o.orderId,
    productionId: o.id,
    product_name: o.product_name || o.productName || "",
    size: rawSize,                     // ✅ display correct (Super King etc)
    sizeNorm: normalizeSize(rawSize),  // ✅ used only for filtering
    storageType: formatStorageType(o.storageType),
    quantity: o.quantity,
    color: o.color || o.fabric_color_name || o.fabricColor || "N/A",
    fabric_name: o.fabric_name || o.fabricName || "",
    fabric_type: o.fabric_type || o.fabricType || "",
    packageCount: typeof o.packageCount === "number" ? o.packageCount : undefined,
    isMerakiDivanOttoman: !!o.isMerakiDivanOttoman,
    referenceNumber: String(
      o.referenceNumber ??
        o.reference_number ??
        o.referenceNo ??
        o.reference ??
        o.customerReference ??
        o.customer_ref ??
        ""
    ),
    orderNote: typeof o.orderNote === "string" ? o.orderNote : o.orderNote?.text || "",

    productionStatus: o.sheetProductionStatus ?? "in progress",
    sheetProductionStatus: o.sheetProductionStatus ?? "in progress",
    priority: o.priority || "normal",
    BarcodeData: o.BarcodeData || "",
    trackingId: o.trackingId || "",
    factory_price: o.factory_price || o.factoryPrice || 0,
    costPrice: o.factory_price || o.factoryPrice || 0,
    sequence_no: o.sequence_no ?? null,
    sequence_month: o.sequence_month ?? null,
    job_name: o.job_name ?? null,
    loadings: Array.isArray(o.loadings) ? o.loadings : [],
    createdAt: o.createdAt ?? null,
    orderDate: o.orderDate ?? null,
    orderStatus: o.orderStatus || null,
    isCancelled: !!o.isCancelled,
    cancelReason: o.cancelReason || null,
    special_quote: o.special_quote ?? o.specialQuote ?? null,
  };
});

      setOrders(formatted);
      // Refresh month/year summary (keeps UI in sync when orders change)
      buildMonthYearFromOrders(formatted);
loadingColorMapRef.current.clear();
nextLoadingColorRef.current = 0;
      const noteMap = {};
      const statusMap = {};
      formatted.forEach((o) => {
  noteMap[o.id] = o.orderNote;
  statusMap[o.productionId] = o.productionStatus || "in progress";
});

      setNotes(noteMap);
      setNewStatuses(statusMap);
    } catch (err) {
      setOrders([]);
      message.error(
        err?.response?.data?.message || err?.message || "Error fetching production orders."
      );
    } finally {
      setLoading(false);
    }
  };

  const buildMonthYearFromOrders = (ordersList) => {
    const monthsMap = {};
    const yearsMap = {};

    ordersList.forEach((o) => {
      const d = o.sequence_month
        ? new Date(o.sequence_month + "-01")
        : o.orderDate
        ? new Date(o.orderDate)
        : null;

      if (!d || isNaN(d)) return;

      const yyyy = String(d.getFullYear());
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const key = `${yyyy}-${mm}`;

      monthsMap[key] = (monthsMap[key] || 0) + 1;
      yearsMap[yyyy] = (yearsMap[yyyy] || 0) + 1;
    });

    setAvailableMonths(Object.entries(monthsMap).map(([month, count]) => ({ month, count })));
    setAvailableYears(yearsMap);
  };

  const fetchAllSizes = async () => {
    try {
      const token = localStorage.getItem("token");
      let res = await axiosInstance.get("/admin/sizes", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      let sizes = [];
      if (Array.isArray(res?.data)) {
        sizes = res.data;
      } else if (Array.isArray(res?.data?.sizes)) {
        sizes = res.data.sizes;
      }

      if (!sizes.length) {
        try {
          res = await axiosInstance.get("/sizes", {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (Array.isArray(res?.data)) sizes = res.data;
          else if (Array.isArray(res?.data?.sizes)) sizes = res.data.sizes;
        } catch {
          /* ignore */
        }
      }

      const names = Array.from(
        new Set(
          sizes.map((s) => (typeof s?.size === "string" ? s.size.trim() : "")).filter(Boolean)
        )
      );

      setAllSizesFromDb(names);
    } catch {
      setAllSizesFromDb([]);
    }
  };

  const fetchProductionMonths = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axiosInstance.get("/admin/production/months", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (Array.isArray(data?.months)) setAvailableMonths(data.months);
      else setAvailableMonths([]);

      if (data?.years && typeof data.years === "object") setAvailableYears(data.years);
      else setAvailableYears({});
    } catch (e) {
      console.warn("Could not fetch production months:", e?.message || e);
      setAvailableMonths([]);
      setAvailableYears({});
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchAllSizes();
  }, []);

  // Build filter choices
  const storageOptions = Array.from(
    new Set([...ALL_STORAGE_TYPES, ...orders.map((o) => o.storageType).filter(Boolean)])
  );
  const priorityOptions = Array.from(
    new Set([
      ...ALL_PRIORITIES,
      ...orders.map((o) => (o.priority || "").toLowerCase()).filter(Boolean),
    ])
  );
  const prodStatusOptions = Array.from(
    new Set([
      ...ALL_PROD_STATUSES,
      ...orders
        .map((o) => (o.sheetProductionStatus || o.productionStatus || "").toLowerCase())
        .filter(Boolean),
    ])
  );
   const loadingOptions = Array.from(
    new Set(
      orders
        .flatMap((o) => (o.loadings || []).map((l) => (l.comment || "").trim()).filter(Boolean))
        .filter(Boolean)
    )
  );

 const normalizeSize = (v) =>
  String(v || "")
    .replace(/\u00a0/g, " ")   // ✅ remove NBSP
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

  const sizeOptions = Array.from(
    new Map(
      [...allSizesFromDb, ...orders.map((o) => o.size)]
        .filter(Boolean)
        .map((s) => [normalizeSize(s), s.trim()])
    ).values()
  );

  const filteredOrders = orders.filter((o) => {
    const s = (search || "").toLowerCase();
    const matchesSearch =
      !s ||
      String(o.sequence_no ?? "").toLowerCase().includes(s) ||
      (formatDate(o.orderDate) || "").toLowerCase().includes(s) ||
      (o.product_name || "").toLowerCase().includes(s) ||
      (o.referenceNumber || "").toLowerCase().includes(s) ||
      (o.trackingId || "").toLowerCase().includes(s) ||
      (o.color || "").toLowerCase().includes(s);

    const matchesStorage = !filterStorage || o.storageType === filterStorage;
    const matchesPriority =
      !filterPriority || (o.priority || "").toLowerCase() === filterPriority.toLowerCase();
    const matchesProdStatus =
      !filterProdStatus ||
      (o.sheetProductionStatus || o.productionStatus || "").toLowerCase() ===
        filterProdStatus.toLowerCase();
        const matchesSize =
  !filterSize || o.sizeNorm === normalizeSize(filterSize);

    // NEW: Loading filter — true if no filter OR any loading comment includes the selected text
    const matchesLoading =
      !filterLoading ||
      (Array.isArray(o.loadings) &&
        o.loadings.some((l) =>
          (l.comment || "").toLowerCase().includes(String(filterLoading || "").toLowerCase())
        ));

    // MONTH / YEAR filtering (new)
    let matchesMonth = true;
    let matchesYear = true;


    const seqMonth = o.sequence_month || null; // expected "YYYY-MM" or null
    const orderDate = o.orderDate || o.createdAt || null;

    if (selectedMonthFilter) {
      // require exact sequence_month match OR if sequence_month is missing, match createdAt's YYYY-MM
      const fallbackMonth = orderDate ? new Date(orderDate).toISOString().slice(0, 7) : null;
      matchesMonth =
        seqMonth === selectedMonthFilter ||
        (seqMonth == null && fallbackMonth === selectedMonthFilter);
    }

    if (selectedYearFilter) {
      // year-only selected: accept sequence_month starting with year OR createdAt year
      if (seqMonth) {
        matchesYear = seqMonth.startsWith(selectedYearFilter);
      } else if (orderDate) {
        try {
          matchesYear = new Date(orderDate).getFullYear() === Number(selectedYearFilter);
        } catch {
          matchesYear = true;
        }
      }
    }

    return (
      matchesSearch &&
      matchesStorage &&
      matchesPriority &&
      matchesProdStatus &&
      matchesLoading &&
      matchesSize &&
      matchesMonth &&
      matchesYear
    );
  });

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const newestFirst = sortOrder !== "oldest";

    // 1) sequence_month (YYYY-MM)
    const am = a.sequence_month || "";
    const bm = b.sequence_month || "";
    if (am !== bm) {
      return newestFirst ? bm.localeCompare(am) : am.localeCompare(bm);
    }

    // 2) sequence_no
    const an = a.sequence_no == null ? -Infinity : Number(a.sequence_no);
    const bn = b.sequence_no == null ? -Infinity : Number(b.sequence_no);
    if (an !== bn) {
      return newestFirst ? bn - an : an - bn;
    }

    // 3) createdAt / orderDate
    const aTime = new Date(a.createdAt || a.orderDate || 0).getTime();
    const bTime = new Date(b.createdAt || b.orderDate || 0).getTime();
    if (aTime !== bTime) {
      return newestFirst ? bTime - aTime : aTime - bTime;
    }

    // 4) FINAL STABLE TIE-BREAKER
    return newestFirst ? b.id - a.id : a.id - b.id;
  });
  const totalQuantity = filteredOrders.reduce(
  (sum, o) => sum + Number(o.quantity || 0),
  0
);

  const handleRemoveSelected = () => {
    if (!selectedRowKeys.length) return;
    confirm({
      title: "Remove selected from Production?",
      content: "This will remove the selected orders from the production list.",
      okText: "Remove",
      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
      onOk: async () => {
        try {
          console.log("Deleting order IDs:", selectedRowKeys);

          await Promise.all(
            selectedRowKeys.map((id) => {
              console.log("Calling API for orderId:", id);
              return axiosInstance.delete(`/admin/production/remove/${id}`);
            })
          );

          message.success("Removed from production.");
          setSelectedRowKeys([]);
          fetchOrders();
        } catch (e) {
          console.error("Delete error:", e);
          message.error(e?.response?.data?.message || "Failed to remove.");
        }
      },
    });
  };
  // ⬇⬇⬇ Bulk update status
 const bulkUpdateStatus = async (status) => {
  if (!selectedRowKeys.length) {
    message.warning("Please select one or more orders first.");
    return;
  }
  try {
    await axiosInstance.patch("/order/production/status", {
      productionIds: selectedRowKeys,
      status,
    });

    message.success(`Updated ${selectedRowKeys.length} items to "${status}".`);
    setSelectedRowKeys([]);
    fetchOrders();
  } catch (e) {
    message.error(e?.response?.data?.message || "Failed to update selected items.");
  }
};

  const bulkStatusMenu = {
    items: [
      { key: "in progress", label: "In Progress" },
      { key: "complete", label: "Complete" },
      { key: "shipped", label: "Shipped" },
    ],
    onClick: ({ key }) => bulkUpdateStatus(key),
  };
  // ⬆⬆⬆ END: Bulk update status

  const extractSheetNumber = (data) => {
    if (!data) return undefined;
    // ✅ NEW: read first element from `numbers: [...]` returned by /order-sheets/issue
    if (Array.isArray(data?.numbers) && data.numbers.length) {
      const n = Number(data.numbers[0]);
      if (Number.isInteger(n)) return n;
    }
    const candidates = [
      data.number,
      data.sheetNumber,
      data.sheet_no,
      data.sheetNo,
      data.id,
      data.orderSheetNumber,
      data?.sheet?.number,
      data?.sheet?.id,
    ];
    for (const v of candidates) {
      const n = Number(v);
      if (Number.isInteger(n) && n >= 0) return n;
    }
    return undefined;
  };

  // ===== Excel export + ARCHIVE UPLOAD =====
 const downloadExcel = async () => {
  if (!selectedRowKeys.length) {
    message.warning("Please select one or more orders to export!");
    return;
  }

  const token = localStorage.getItem("token");
  let osNumber;

  // ---- Resolve selectedRowKeys -> actual order IDs (o.id)
  // Handles cases where rowKey is productionId or id.
  let selectedOrderIds = orders
    .filter((o) =>
      selectedRowKeys.some(
        (k) => String(k) === String(o.id) || String(k) === String(o.productionId)
      )
    )
    .map((o) => o.id);

  // Fallback: if mapping produced nothing, assume selectedRowKeys are already order IDs
  if (!selectedOrderIds.length) {
    selectedOrderIds = selectedRowKeys.slice();
    console.warn(
      "downloadExcel: couldn't map selectedRowKeys to orders; falling back to selectedRowKeys as orderIds",
      { selectedRowKeys }
    );
  }

  try {
    // Reserve a sheet number (row in OrderSheets) — pass real order IDs
    const { data } = await axiosInstance.post(
      "/admin/order-sheets/issue",
      { orderIds: selectedOrderIds },
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    osNumber = extractSheetNumber(data);
    if (osNumber === undefined) {
      try {
        const peek = await axiosInstance.get("/admin/order-sheets/recent", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        osNumber =
          extractSheetNumber(peek.data) ||
          extractSheetNumber(peek.data?.sheets?.[0]) ||
          Number(peek.data?.last);
      } catch {
        /* ignore */
      }
    }
  } catch (err) {
    message.warning(
      err?.response?.data?.message ||
        "Couldn't reserve an Order Sheet number. Exporting without a number…"
    );
  }

  // select orders to export — use resolved selectedOrderIds so exportData is not empty
  let exportData = orders.filter((o) => selectedOrderIds.includes(o.id));

  // Sort exportData to ensure stable order in export:
  // primary: sequence_month (YYYY-MM) asc, then sequence_no asc, then createdAt, then id
  exportData.sort((a, b) => {
    const am = a.sequence_month || "";
    const bm = b.sequence_month || "";
    if (am && bm && am !== bm) return am.localeCompare(bm);
    // same month (or both empty)
    const an = Number(a.sequence_no || 0);
    const bn = Number(b.sequence_no || 0);
    if (a.sequence_no && b.sequence_no && an !== bn) return an - bn;
    if (a.sequence_no && !b.sequence_no) return -1;
    if (!a.sequence_no && b.sequence_no) return 1;
    // fallback to createdAt or id (ensure deterministic)
    const aTime = new Date(a.createdAt || a.orderDate || 0).getTime();
    const bTime = new Date(b.createdAt || b.orderDate || 0).getTime();
    if (aTime && bTime && aTime !== bTime) return aTime - bTime;
    return (a.id || 0) - (b.id || 0);
  });

  const hasAnyNote = exportData.some((o) => String(o.orderNote || "").trim().length > 0);

  // Group by product_name but keep order (we iterate exportData sequentially)
  const groups = exportData.reduce((acc, o) => {
    const key = (o.product_name || "").trim().toLowerCase();
    if (!acc[key]) {
      acc[key] = { displayName: (o.product_name || "").trim(), items: [] };
    }
    acc[key].items.push(o);
    return acc;
  }, {});

  const colLetter = (n) => String.fromCharCode(64 + n);

  try {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Order Sheet");

    const titleText =
      osNumber !== undefined && osNumber !== null ? `ORDER SHEET ${osNumber}` : "ORDER SHEET —";

    const headersBase = ["Sr.No", "Size", "Product", "Fabric/ Color", "Quantity", "Storage Type"];
    const headers = hasAnyNote ? [...headersBase, "Special Notes"] : headersBase;

    const lastCol = colLetter(headers.length);
    const titleRow = ws.addRow([titleText]);
    ws.mergeCells(`A1:${lastCol}1`);
    titleRow.getCell(1).font = { bold: true, size: 16 };
    titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };

    ws.addRow([]);

    const header = ws.addRow(headers);
    header.font = { bold: true };
    header.alignment = { horizontal: "center", vertical: "middle" };
    header.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFBDD7EE" } };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    const commonWidth = 15;
    ws.columns = hasAnyNote
      ? [
          { key: "sr_no", width: 20 },
          { key: "size", width: commonWidth },
          { key: "product_name", width: commonWidth },
          { key: "color", width: commonWidth },
          { key: "quantity", width: commonWidth },
          { key: "storageType", width: commonWidth },
          { key: "orderNote", width: commonWidth },
        ]
      : [
          { key: "sr_no", width: 20 },
          { key: "size", width: commonWidth },
          { key: "product_name", width: commonWidth },
          { key: "color", width: commonWidth },
          { key: "quantity", width: commonWidth },
          { key: "storageType", width: commonWidth },
        ];

    // fallback counter for rows that don't have sequence_no
    let fallbackCounter = 1;

    // Track product change to insert gap only when needed
    let previousProduct = null;

    for (const o of exportData) {
      const currentProduct = (o.product_name || "").trim();

      // Insert ONE blank row ONLY when product changes (not first row)
      if (previousProduct && currentProduct !== previousProduct) {
        ws.addRow([]);
      }

      // compute srNo: prefer stored sequence; fallback to incremental counter
      let srNo;
      if (o.sequence_no != null) {
        srNo = formatMonthSequence(o.sequence_month, o.sequence_no);
      } else {
        srNo = `TEMP · ${fallbackCounter++}`;
      }

      const fabricText = `${o.fabric_type || ""} ${o.fabric_name || ""}\n${o.color || ""}`.trim();

      const rowValues = hasAnyNote
        ? [
            String(srNo),
            o.size,
            currentProduct,
            fabricText,
            o.quantity,
            o.storageType,
            o.orderNote || "",
          ]
        : [String(srNo), o.size, currentProduct, fabricText, o.quantity, o.storageType];

      const row = ws.addRow(rowValues);

      row.eachCell((cell) => {
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      if ((o.priority || "").toLowerCase() === "urgent") {
        row.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFFE5E5" },
          };
        });
      }

      previousProduct = currentProduct;
    }

    const buf = await wb.xlsx.writeBuffer();
    const fileName =
      osNumber !== undefined && osNumber !== null ? `Order_Sheet_${osNumber}.xlsx` : "Order_Sheet.xlsx";

    // Archive the order sheet so it appears in the Manage page
    try {
      const form = new FormData();
      form.append(
        "file",
        new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        fileName
      );
      if (osNumber !== undefined && osNumber !== null) {
        // server expects 'sheetId'
        form.append("sheetId", String(osNumber));
      }
      // <-- Use selectedOrderIds (actual order ids) here
      form.append("orderIds", JSON.stringify(selectedOrderIds));
      form.append("hasNotes", String(hasAnyNote));

      await axiosInstance.post("/admin/order-sheets/upload", form, {
        headers: token
          ? { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
          : { "Content-Type": "multipart/form-data" },
        transformRequest: (d) => d,
      });
    } catch (e) {
      console.error("Archive upload failed", e);
      message.warning("Order Sheet created but not archived on server (upload failed).");
    }

    // Local download (unchanged)
    saveAs(new Blob([buf]), fileName);
    message.success("Order Sheet downloaded and archived.");
  } catch (err) {
    console.error(err);
    message.error("Error generating Order Sheet.");
  }
};

  // ======================

  const updateOrderNote = async (id) => {
    try {
      await axiosInstance.put(`/order/note/${id}`, { orderNote: notes[id] || "" });
      message.success("Order note updated!");
      fetchOrders();
    } catch {
      message.error("Error updating order note.");
    }
  };

  const updateProductionStatus = async (productionId, status) => {
  try {
    await axiosInstance.patch("/order/production/status", {
      productionId,
      status,
    });
  } catch (e) {
    message.error(e?.response?.data?.message || "Error updating production status.");
  }
};

  // ==== CHAT HELPERS (no polling) ====
  const scrollChatToBottom = () => {
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  const openChat = async (order) => {
    setChatOrder(order);
    setChatVisible(true);
    await fetchChatMessages(order.id, { scroll: true });
  };

  const closeChat = () => {
    setChatVisible(false);
    setChatOrder(null);
    setChatMessages([]);
    setChatText("");
    if (chatAbortRef.current) {
      chatAbortRef.current.abort();
      chatAbortRef.current = null;
    }
  };

  const fetchChatMessages = async (orderId, opts = { scroll: false }) => {
    if (!orderId) return;

    if (chatAbortRef.current) chatAbortRef.current.abort();
    const ctrl = new AbortController();
    chatAbortRef.current = ctrl;

    try {
      setChatLoading(true);
      const { data } = await axiosInstance.get("/order/chat/messages", {
        params: { orderId },
        signal: ctrl.signal,
      });
      const msgs = Array.isArray(data?.messages) ? data.messages : Array.isArray(data) ? data : [];
      setChatMessages(msgs);
      if (opts.scroll) setTimeout(scrollChatToBottom, 50);
    } catch (e) {
      /* ignore */
    } finally {
      setChatLoading(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatOrder || !chatText.trim()) return;
    setChatSending(true);
    try {
      await axiosInstance.post("/order/chat/messages", {
        orderId: chatOrder.id,
        text: chatText.trim(),
      });
      setChatText("");
      await fetchChatMessages(chatOrder.id, { scroll: true });
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to send message");
    } finally {
      setChatSending(false);
    }
  };

  // ================= PRODUCTION LOADING (NEW) =================

  // open modal to add loading for selected rows (bulk)
  const openLoadingModalForSelection = () => {
    if (!selectedRowKeys || !selectedRowKeys.length) {
      message.warning("Please select one or more orders first.");
      return;
    }
    setLoadingModalTargetIds(selectedRowKeys.slice());
    setLoadingModalComment("");
    setLoadingModalVisible(true);
  };

  // open modal to add loading for single row
  const openLoadingModalForRecord = (record) => {
    setLoadingModalTargetIds([record.id]);
    setLoadingModalComment("");
    setLoadingModalVisible(true);
  };

  // create loading (single or bulk). Backend accepted payloads:
  // - { productionId } OR { orderIds: [id, ...], comment }
  // We POST orderIds + comment for bulk; for single we send orderIds too (server should accept).
  const createLoading = async ({ orderIds = [], comment = "" }) => {
    if (!orderIds || !orderIds.length) {
      message.warning("No orders selected for loading.");
      return;
    }
    setLoadingCreating(true);
    try {
      const token = localStorage.getItem("token");
      // Try to send orderIds + comment which is more flexible for bulk creation.
      const { data } = await axiosInstance.post(
        "/admin/production/loading",
        { orderIds, comment },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      // assume server returns success and possibly the created loadings/order updates
      message.success("Loading created");
      setLoadingModalVisible(false);
      setLoadingModalComment("");
      setLoadingModalTargetIds([]);
      setSelectedRowKeys([]); // clear selection after grouping
      fetchOrders();
      return data;
    } catch (e) {
      console.error("createLoading error", e);
      message.error(e?.response?.data?.message || "Failed to create loading");
      throw e;
    } finally {
      setLoadingCreating(false);
    }
  };

  const addProductionLoading = async (productionId) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.post(
        "/admin/production/loading",
        { productionId },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      message.success("Loading added");
      fetchOrders(); // refresh list
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to add loading");
    }
  };

  const removeProductionLoading = async (loadingId) => {
    confirm({
      title: "Remove this loading?",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = localStorage.getItem("token");
          await axiosInstance.delete(`/admin/production/loading/${loadingId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          message.success("Loading removed");
          // Update local details list if visible
          setLoadingDetailsList((prev) => {
            const next = (prev || []).filter((l) => String(l.id) !== String(loadingId));
            // if empty, close modal
            if (!next.length) setLoadingDetailsVisible(false);
            return next;
          });
          fetchOrders();
        } catch (e) {
          message.error(e?.response?.data?.message || "Failed to remove loading");
        }
      },
    });
  };

  // open single loading in a details modal (read-only)
  const openSingleLoading = (loading, e) => {
    if (e?.stopPropagation) e.stopPropagation();
    setLoadingDetailsList([loading]);
    setLoadingDetailsVisible(true);
  };
  // open all loadings for a record
  const openLoadingDetailsForRecord = (record, e) => {
    if (e?.stopPropagation) e.stopPropagation();
    setLoadingDetailsList(record.loadings || []);
    setLoadingDetailsVisible(true);
  };

  useEffect(() => {
    if (chatVisible) scrollChatToBottom();
  }, [chatMessages, chatVisible]);

  const columns = [
    {
      title: "No",
      key: "srno",
      dataIndex: "sequence_no",
      width: 80,
      render: (seq, record) => {
        if (seq == null || seq === "") return <div className="text-center">-</div>;
        return <div className="text-center font-medium text-gray-700">{String(seq)}</div>;
      },
    },

    {
      title: "Date",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 120,
      render: (date) => {
        // `date` is the raw backend value (ISO or timestamp). Format here for display.
        const dt = new Date(date);
        const val = isNaN(dt)
          ? String(formatDate(date) || "-")
          : dt.toLocaleDateString(undefined, { year: "2-digit", month: "2-digit", day: "2-digit" });
        return (
          <div className="text-sm text-gray-600 font-medium bg-gray-50 px-2 py-1 rounded text-center">
            {val}
          </div>
        );
      },
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      width: 120,
      render: (size) => <div className="text-center font-medium text-gray-700">{size}</div>,
    },
    {
      title: "Product",
      dataIndex: "product_name",
      key: "product_name",
      width: 120,
    },
    {
      title: "Fabric / Color",
      key: "fabric_color",
      width: 160,
      render: (_, record) => {
        const fabricName = record.fabric_name || "-";
        const color = record.color;

        if (!color) {
          return <div className="text-xs text-gray-500">{fabricName}</div>;
        }

        const key = color.trim().toLowerCase();

        const colorMap = {
          silver: "#C0C0C0",
          steel: "#7A8B8B",
          "pebble / coffee": "#A68A7C",
          pebble: "#A68A7C",
          coffee: "#6F4E37",
          black: "#000000",
          ivory: "#FFFFF0",
          beige: "#F5F5DC",
          "duck-egg": "#CFECEC",
          gray: "#808080",
          grey: "#808080",
          "armour dove": "#7E8187",
          "biscuit dove": "#D9C9A3",
          "coal dove": "#3B3B3B",
          "coral dove": "#E9967A",
          "emerald dove": "#50C878",
          "ice dove": "#DDEFFF",
          "rose dove": "#EAB1C0",
          "stone dove": "#A8A8A8",
          "teal dove": "#008080",
          mallard: "#214C4F",
          asphalt: "#4A4A4A",
          lilac: "#C8A2C8",
          "lilac ": "#C8A2C8",
          sand: "#E4C590",
          mink: "#BCA894",
          cream: "#FFFDD0",
          pink: "#FFC0CB",
          "teddy ivory": "#F8F6EF",
          "teddy cream": "#FFFBEA",
          "steel plush": "#9AA0A6",
          forest: "#228B22",
          mustard: "#FFDB58",
          slate: "#708090",
          "new silver": "#C9C9C9",
          "new coffee": "#8B6A4E",
          blue: "#1677FF",
          "blue ": "#1677FF",
          charcoal: "#36454F",
          white: "#FFFFFF",
          "sky blue": "#87CEEB",
          sky: "#87CEEB",
          "gray ": "#808080",
        };

        const bg = colorMap[key] || "#E5E7EB";

        const isLight = (() => {
          const c = bg.substring(1);
          const rgb = parseInt(c, 16);
          const r = (rgb >> 16) & 0xff;
          const g = (rgb >> 8) & 0xff;
          const b = rgb & 0xff;
          const brightness = (r * 299 + g * 587 + b * 114) / 1000;
          return brightness > 150;
        })();

        const textColor = isLight ? "#000" : "#fff";

        return (
          <div className="flex flex-col gap-1 items-center">
            {/* ✅ Fabric name (top) */}
            <div className="text-xs font-semibold text-gray-700 text-center">{fabricName}</div>

            {/* ✅ Color badge (bottom) */}
            <div
              className="inline-flex items-center justify-center px-2 py-1 rounded-md border text-xs font-medium text-center shadow-sm"
              style={{
                backgroundColor: bg,
                color: textColor,
                borderColor: "#d1d5db",
                minWidth: 70,
                textTransform: "capitalize",
              }}
            >
              {color.trim()}
            </div>
          </div>
        );
      },
    },
    {
      title: "Storage",
      dataIndex: "storageType",
      key: "storageType",
      width: 100,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 90,
      render: (priority) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-bold ${
            priority === "urgent"
              ? "bg-red-100 text-red-800 border border-red-200"
              : "bg-green-100 text-green-800 border-green-200 border"
          }`}
        >
          {priority}
        </span>
      ),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 70,
      render: (quantity) => <div className="text-center font-bold text-gray-700">{quantity}</div>,
    },
    {
      title: "Customer Ref",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
      width: 130,
      render: (val) => {
        const text = String(val ?? "-");

        const handleCopy = (e) => {
          if (e?.clipboardData) {
            e.clipboardData.setData("text/plain", text);
            e.preventDefault();
          }
        };

        return (
          <div
            className="font-mono text-sm bg-gray-50 px-2 py-1 rounded border border-gray-200 whitespace-nowrap"
            title={text}
            onCopy={handleCopy}
          >
            <span className="block max-w-[10ch] truncate">{text}</span>
          </div>
        );
      },
    },
    {
      title: "Special Notes",
      key: "specialInstruction",
      width: 220,
      render: (_, record) => {
        // Production Manager: read-only (can only view)
        if (isProductionManager) {
          return (
            <div className="py-1">
              {notes[record.id] ? (
                <p className="m-0 break-words text-sm">{notes[record.id]}</p>
              ) : (
                <em className="text-gray-400 text-sm">No note</em>
              )}
            </div>
          );
        }

        // Sales User: read-only
        if (isSalesUser) {
          return (
            <div className="py-1">
              {notes[record.id] ? (
                <p className="m-0 break-words text-sm">{notes[record.id]}</p>
              ) : (
                <em className="text-gray-400 text-sm">No note</em>
              )}
            </div>
          );
        }

        // Production Admin & other roles: editable
        return (
          <div className="flex gap-2 items-start">
            <TextArea
              value={notes[record.id] || ""}
              onChange={(e) =>
                setNotes((p) => ({
                  ...p,
                  [record.id]: e.target.value,
                }))
              }
              autoSize={{ minRows: 1, maxRows: 3 }}
              placeholder="Add order note..."
              className="flex-1 text-sm"
            />
            <Button
              type="primary"
              size="small"
              onClick={() => updateOrderNote(record.id)}
              className="bg-blue-500 hover:bg-blue-600 border-blue-500"
            >
              Save
            </Button>
          </div>
        );
      },
    },

    {
  title: "Special Instructions",
  key: "special_quote",
  width: 200,
  render: (_, record) => {
    const special = normalizeSpecialQuote(record);
    if (!special.hasAny) {
      return <span className="text-gray-400 text-sm italic">None</span>;
    }

    const text = String(special.text || "").trim();
    const files = Array.isArray(special.files) ? special.files : [];
    const pdfs = files.filter((f) => f.isPdf);
    const imgs = files.filter((f) => f.isImage && !f.isPdf);
    const others = files.filter((f) => !f.isPdf && !f.isImage);

    return (
      <div className="text-xs leading-snug">
        {text ? (
          <div
            className="text-gray-800 whitespace-pre-wrap"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            title={text}
          >
            {text}
          </div>
        ) : null}

        {(pdfs.length || imgs.length || others.length) ? (
          <div className="mt-1 flex flex-col gap-1">
            {pdfs.slice(0, 2).map((f, idx) => (
              <a
                key={`${f.url}-${idx}`}
                href={f.url}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                📄 {f.name || "Open PDF"}
              </a>
            ))}

            {imgs.slice(0, 2).map((f, idx) => (
              <a
                key={`${f.url}-${idx}`}
                href={f.url}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                🖼️ {f.name || "View image"}
              </a>
            ))}

            {others.slice(0, 1).map((f, idx) => (
              <a
                key={`${f.url}-${idx}`}
                href={f.url}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                📎 {f.name || "Open file"}
              </a>
            ))}

            {files.length > 5 ? (
              <div className="text-gray-500">+{files.length - 5} more</div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  },
},
   {
  title: "Production Status",
  key: "newProductionStatus",
  width: 130,
  render: (_, record) => {

    // 🔴 IF CANCELLED → SHOW LOCKED STATUS
    if (record.isCancelled) {
      return (
        <div className="px-2 py-1 text-xs font-bold text-red-700 bg-red-100 border border-red-300 rounded text-center">
          Cancelled
        </div>
      );
    }

    // ✅ NORMAL ORDERS
    return (
      <Select
        value={newStatuses[record.productionId]}
        onChange={(v) => {
          setNewStatuses((p) => ({ ...p, [record.productionId]: v }));
          updateProductionStatus(record.productionId, v);
        }}
        className="w-full"
        size="small"
      >
        <Option value="in progress">In Progress</Option>
        <Option value="complete">Complete</Option>
        <Option value="shipped">Shipped</Option>
      </Select>
    );
  },
},

{
  title: "Loading",
  key: "loading",
  width: 140,
  render: (_, record) => {
    const loadings = Array.isArray(record.loadings) ? record.loadings : [];
    if (!loadings.length) {
      return <div className="loading-card-empty">Pending</div>;
    }

    const first = loadings[0];
    const comment = (first.comment || "").trim().toLowerCase();

    // ✅ Extract number from comment like "loading 1", "loading 2"
    let colorIndex = 0; // default → Loading 1 (green)

    const match = comment.match(/\b(\d+)\b/);
    if (match) {
      const loadingNumber = Number(match[1]); // 1, 2, 3...
      if (!isNaN(loadingNumber) && loadingNumber > 0) {
        colorIndex = loadingNumber - 1;
      }
    }

    // ✅ Ensure index is inside array range
    colorIndex = colorIndex % LOADING_COLORS.length;

    const leftColor = LOADING_COLORS[colorIndex];
    const moreCount = Math.max(0, loadings.length - 1);

    return (
      <div
        className="loading-card-final"
        style={{ borderLeftColor: leftColor }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="loading-card-title">
          {first.comment || "Loading"}
        </div>

        <div className="loading-card-date">
          {formatDate(first.createdAt)}
        </div>

        <button
          className="loading-card-view"
          onClick={(e) => {
            e.stopPropagation();
            openSingleLoading(first, e);
          }}
        >
          View
        </button>

        {moreCount > 0 && (
          <div className="loading-card-more">+{moreCount}</div>
        )}
      </div>
    );
  },
} ,






    // Hide Label actions for Production Admin
    ...(!isProductionAdmin
      ? [
          {
            title: "Label",
            key: "label",
            width: 100,
            render: (_, record) => (
              <div className="flex gap-1 justify-center">
                <Tooltip title="Print Label">
                  <Button
                    size="small"
                    icon={<PrinterOutlined />}
                    onClick={() => generateLabelPDF(record, "print")}
                    className="bg-purple-500 hover:bg-purple-600 border-purple-500 text-white"
                  />
                </Tooltip>
                <Tooltip title="Download Label">
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => generateLabelPDF(record, "download")}
                    className="bg-green-500 hover:bg-green-600 border-green-500 text-white"
                  />
                </Tooltip>
              </div>
            ),
          },
        ]
      : []),
    // Hide Chat for Production Admin
    {
      title: "Chat",
      key: "chat",
      width: 80,
      render: (_, record) => (
        <Tooltip title="Chat with team about this order">
          <Button
            size="small"
            icon={<MessageOutlined />}
            onClick={() => openChat(record)}
            className="border-blue-500 text-blue-600"
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large" className="min-h-screen">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Production Orders</h1>
                <p className="text-gray-600">Manage production workflow and order tracking</p>
              </div>

              <div className="flex flex-wrap gap-3">
                {selectedRowKeys.length > 0 && (
                  <>
                    <Dropdown menu={bulkStatusMenu} trigger={["click"]}>
                      <Button
                        type="primary"
                        icon={<FileTextOutlined />}
                        className="h-10 px-4 bg-blue-500 hover:bg-blue-600 border-blue-500"
                      >
                        Update Status
                      </Button>
                    </Dropdown>

                    {/* Hide remove for Production Admin */}
                    {!isProductionAdmin && (
                      <Tooltip title="Remove selected orders from production">
                        <Button
                          type="primary"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={handleRemoveSelected}
                          className="h-10 px-4"
                        >
                          Remove Selected
                        </Button>
                      </Tooltip>
                    )}
                  </>
                )}

                {/* ✅ Invoice Selected REMOVED (kept everything else same). */}
                {/* Hide order sheet button for Production Admin */}
                {!isProductionAdmin && (
                  <>
                    <Tooltip title="Download Order Sheet">
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={downloadExcel}
                        className="bg-green-500 hover:bg-green-600 border-green-500 h-10 px-4"
                      >
                        Order Sheet
                      </Button>
                    </Tooltip>

                    {/* Top Add Loading (bulk) */}
                    <Tooltip title="Group selected orders into a Loading">
                      <Button
                        type="default"
                        icon={<FileTextOutlined />}
                        onClick={openLoadingModalForSelection}
                        disabled={!selectedRowKeys.length}
                        className="h-10 px-4 ml-2 border-gray-200"
                      >
                        Add Loading
                      </Button>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <SearchOutlined className="mr-2" />
                  Search
                </label>
                <Input
                  placeholder="Search orders..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  prefix={<SearchOutlined className="text-gray-400" />}
                  className="w-full h-10 rounded-lg"
                  allowClear
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FilterOutlined className="mr-2" />
                  Storage
                </label>
                <Select
                  allowClear
                  placeholder="Storage Type"
                  value={filterStorage}
                  onChange={setFilterStorage}
                  className="w-full h-10 rounded-lg"
                  showSearch
                  optionFilterProp="children"
                >
                  {storageOptions.map((opt) => (
                    <Option value={opt} key={opt}>
                      {opt}
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FilterOutlined className="mr-2" />
                  Priority
                </label>
                <Select
                  allowClear
                  placeholder="Priority"
                  value={filterPriority}
                  onChange={setFilterPriority}
                  className="w-full h-10 rounded-lg"
                  showSearch
                  optionFilterProp="children"
                >
                  {priorityOptions.map((opt) => (
                    <Option value={opt} key={opt}>
                      {opt}
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FileTextOutlined className="mr-2" />
                  Status
                </label>
                <Select
                  allowClear
                  placeholder="Prod Status"
                  value={filterProdStatus}
                  onChange={setFilterProdStatus}
                  className="w-full h-10 rounded-lg"
                  showSearch
                  optionFilterProp="children"
                >
                  {prodStatusOptions.map((opt) => (
                    <Option value={opt} key={opt}>
                      {opt}
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FilterOutlined className="mr-2" />
                  Size
                </label>
                <Select
                  allowClear
                  placeholder="Size"
                  value={filterSize}
                  onChange={setFilterSize}
                  className="w-full h-10 rounded-lg"
                  showSearch
                  optionFilterProp="children"
                  dropdownStyle={sizeOptions.length > 6 ? { maxHeight: 240, overflowY: "auto" } : undefined}
                >
                  {sizeOptions.map((opt) => (
                    <Option value={opt} key={opt}>
                      {opt}
                    </Option>
                  ))}
                </Select>
              </div>
              {/* Year Filter */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FilterOutlined className="mr-2" />
                  Year
                </label>
                <Select
                  allowClear
                  placeholder="Year"
                  value={selectedYearFilter}
                  onChange={(v) => {
                    setSelectedYearFilter(v);
                    // when year changes, clear month selection
                    setSelectedMonthFilter(undefined);
                  }}
                  className="w-full h-10 rounded-lg"
                  showSearch
                  optionFilterProp="children"
                >
                  {Object.keys(availableYears)
                    .sort((a, b) => Number(b) - Number(a))
                    .map((y) => (
                      <Option value={y} key={y}>
                        {y}
                      </Option>
                    ))}
                </Select>
              </div>

              {/* Month Filter */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FilterOutlined className="mr-2" />
                  Month
                </label>
                <Select
                  allowClear
                  placeholder="Month"
                  value={selectedMonthFilter}
                  onChange={(v) => setSelectedMonthFilter(v)}
                  className="w-full h-10 rounded-lg"
                  showSearch
                  optionFilterProp="children"
                >
                  {(selectedYearFilter
                    ? availableMonths.filter((m) => m.month && m.month.startsWith(selectedYearFilter))
                    : availableMonths
                  ).map((m) => (
                    <Option value={m.month} key={m.month}>
                      {monthLabel(m.month)} ({m.count})
                    </Option>
                  ))}
                </Select>
              </div>
              {/* Loading Filter */}
<div className="flex flex-col">
  <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
    <FilterOutlined className="mr-2" />
    Loading
  </label>
  <Select
    allowClear
    placeholder="Loading"
    value={filterLoading}
    onChange={setFilterLoading}
    className="w-full h-10 rounded-lg"
    showSearch
    optionFilterProp="children"
  >
    {loadingOptions.map((opt) => (
      <Option value={opt} key={opt}>
        {opt}
      </Option>
    ))}
  </Select>
</div>


              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FilterOutlined className="mr-2" />
                  Sort
                </label>

                <Select value={sortOrder} onChange={setSortOrder} className="w-full h-10 rounded-lg">
                  <Option value="newest">Newest First</Option>
                  <Option value="oldest">Oldest First</Option>
                </Select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  Summary
                </label>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 h-10 flex items-center">
                  <div className="text-sm font-medium text-gray-900">
  Total Qty: {totalQuantity}
</div>
                </div>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <Table
              dataSource={sortedOrders}
              columns={columns}
             rowKey="productionId"
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
              }}
              pagination={{
                current: currentPage,
                pageSize,
                total: filteredOrders.length,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                className: "px-6 py-4",
                pageSizeOptions: ["15", "30", "50", "100"],
                onChange: (page) => setCurrentPage(page),
              }}
              scroll={{ x: "max-content" }}
              className="modern-orders-table"
              rowClassName={(record) => {
                const baseClasses = "hover:bg-gray-50 transition-colors duration-200";
                // Cancelled rows should be highlighted red
  if (record.isCancelled) {
    // visually consistent with your urgent rows but red
    return `${baseClasses} bg-red-50 border-l-4 border-l-red-400`;
  }
                if ((record.priority || "").toLowerCase() === "urgent") {
                  return `${baseClasses} bg-orange-50 border-l-4 border-l-orange-400`;
                }
                return baseClasses;
              }}
              locale={{
                emptyText: (
                  <div className="text-center py-8">
                    <div className="text-lg font-medium text-gray-500 mb-2">No production orders found</div>
                    <div className="text-sm text-gray-400">Try adjusting your search or filters</div>
                  </div>
                ),
              }}
            />
          </div>
        </div>
      </div>

      {/* Chat modal kept as-is but it's never opened for Production Admin because the Chat column is hidden */}
      <Modal
        title={
          chatOrder ? (
            <div className="flex items-center justify-between w-full pr-8">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                {/* Order Avatar */}
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-sm">#{String(chatOrder.id).slice(-2)}</span>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                  </div>
                </div>

                {/* Order Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-lg font-bold text-gray-900 truncate">Order #{chatOrder.id}</h3>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5"></span>
                      Online
                    </span>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center px-3 py-1 rounded-lg bg-blue-50 border border-blue-200 text-sm font-medium text-blue-700">
                      {chatOrder.product_name || "No product"}
                    </span>

                    <button
                      onClick={() => copyRef(chatOrder.referenceNumber)}
                      className="inline-flex items-center px-3 py-1 rounded-lg bg-gray-50 border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-colors duration-200 group"
                      title="Click to copy reference"
                    >
                      <span className="text-gray-400 mr-1.5">Ref:</span>
                      {chatOrder.referenceNumber || "N/A"}
                      <svg className="w-3 h-3 ml-1.5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 ml-4">
                <Tooltip title="Refresh messages">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => fetchChatMessages(chatOrder?.id, { scroll: true })}
                    className="flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                    size="middle"
                  />
                </Tooltip>
              </div>
            </div>
          ) : (
            <div className="text-gray-900 font-semibold">Chat</div>
          )
        }
        open={chatVisible}
        onCancel={closeChat}
        footer={null}
        width={800}
        className="modern-chat-modal"
        styles={{
          body: {
            padding: "0",
            borderRadius: "12px",
          },
          content: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          },
          header: {
            padding: "20px 24px",
            borderBottom: "1px solid #e5e7eb",
            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
            borderRadius: "16px 16px 0 0",
            position: "relative",
          },
        }}
        destroyOnClose
        closeIcon={
          <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 flex items-center justify-center group">
            <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        }
      >
        {/* Rest of your chat content remains exactly the same */}
        <div className="flex flex-col h-[640px] bg-white">
          {/* MESSAGES CONTAINER */}
          <div
            ref={chatScrollRef}
            className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50 to-blue-50/30 p-6"
            style={{
              backgroundImage: "radial-gradient(circle at 1px 1px, rgba(0, 0, 0, 0.05) 1px, transparent 0)",
              backgroundSize: "20px 20px",
            }}
          >
            {chatLoading ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <Spin size="large" />
                  <p className="mt-3 text-gray-500 text-sm">Loading messages...</p>
                </div>
              </div>
            ) : chatMessages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <MessageOutlined className="text-2xl text-gray-400" />
                </div>
                <div className="text-sm font-medium text-gray-500 mb-1">No messages yet</div>
                <div className="text-xs text-gray-400">Start the conversation</div>
              </div>
            ) : (
              <div className="space-y-4">
                {chatMessages.map((m, idx) => {
                  const mine = Number(m.senderId) === loggedInUserId;
                  const prev = chatMessages[idx - 1];
                  const showDay = !prev || !sameDay(prev?.createdAt, m?.createdAt);
                  const showAvatar =
                    !mine && (!prev || prev.senderRole !== m.senderRole || !sameDay(prev?.createdAt, m?.createdAt));

                  // FIXED FULL NAME MERGE
                  const senderFullName = m.senderName || m.full_name || m.sender?.full_name || m.senderRole || "User";

                  const senderRole = m.senderRole || m.role || "";

                  return (
                    <div key={m.id || `${m.createdAt}-${idx}`} className="message-group">
                      {showDay && (
                        <div className="flex items-center justify-center my-6">
                          <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-2 shadow-sm">
                            <div className="text-xs font-medium text-gray-500">{prettyDay(m.createdAt)}</div>
                          </div>
                        </div>
                      )}

                      <div className={`flex items-start gap-3 ${mine ? "flex-row-reverse" : ""}`}>
                        {/* Avatar */}
                        {!mine && (
                          <div
                            className={`flex-shrink-0 transition-opacity duration-200 ${
                              showAvatar ? "opacity-100" : "opacity-0"
                            }`}
                          >
                            {showAvatar && (
                              <div
                                className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-sm border border-white"
                                title={senderFullName}
                              >
                                <span className="text-white text-xs font-bold">{getInitials(senderFullName)}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Message Bubble */}
                        <div className={`flex flex-col ${mine ? "items-end" : "items-start"} max-w-[70%]`}>
                          {/* Sender Name */}
                          {!mine && showAvatar && (
                            <div className="flex items-center gap-2 mb-1 px-2">
                              <span className="text-xs font-semibold text-gray-700">{senderFullName}</span>

                              {senderRole && (
                                <>
                                  <span className="text-xs text-gray-400">•</span>
                                  <span className="text-xs text-gray-500 capitalize">{senderRole.toLowerCase()}</span>
                                </>
                              )}
                            </div>
                          )}

                          {/* Message */}
                          <div
                            className={`relative px-4 py-3 rounded-2xl shadow-sm ${
                              mine
                                ? "bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-br-md"
                                : "bg-white text-gray-900 border border-gray-200 rounded-bl-md"
                            }`}
                          >
                            <div className={`text-sm leading-relaxed ${mine ? "text-white" : "text-gray-800"}`}>
                              {m.text}
                            </div>

                            {/* Timestamp */}
                            <div className={`text-xs mt-2 flex items-center gap-1 ${mine ? "text-blue-100" : "text-gray-500"}`}>
                              {prettyTime(m.createdAt)}
                              {mine && (
                                <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>

                            {/* Bubble Tail */}
                            <div
                              className={`absolute bottom-0 w-3 h-3 ${
                                mine
                                  ? "right-0 transform translate-x-1/2 bg-blue-500 rotate-45"
                                  : "left-0 transform -translate-x-1/2 bg-white border-l border-b border-gray-200 rotate-45"
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* INPUT AREA */}
          <div className="border-t border-gray-200 bg-white p-4">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Input.TextArea
                  value={chatText}
                  onChange={(e) => setChatText(e.target.value)}
                  onKeyDown={onChatKeyDown}
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  maxLength={2000}
                  showCount={{
                    formatter: ({ count, maxLength }) => `${count}/${maxLength}`,
                  }}
                  placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
                  className="rounded-xl border-gray-300 hover:border-blue-400 focus:border-blue-500 resize-none"
                  styles={{
                    textarea: {
                      borderRadius: "12px",
                      padding: "12px 16px",
                      fontSize: "14px",
                      lineHeight: "1.4",
                    },
                  }}
                />
              </div>
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={chatSending}
                disabled={!chatOrder || !chatText.trim()}
                onClick={sendChatMessage}
                className="h-[48px] px-6 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 border-none shadow-sm hover:shadow-md transition-all duration-200"
                styles={{
                  icon: {
                    marginRight: "6px",
                  },
                }}
              >
                Send
              </Button>
            </div>
          </div>
        </div>

        {/* Custom styles for the chat */}
        <style jsx>{`
          .modern-chat-modal .ant-modal-content {
            border-radius: 16px;
            overflow: hidden;
          }

          .modern-chat-modal .ant-modal-header {
            border-bottom: 1px solid #e5e7eb;
            border-radius: 16px 16px 0 0;
            padding: 20px 24px;
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            margin-bottom: 0;
          }

          .modern-chat-modal .ant-modal-title {
            font-size: 18px;
            font-weight: 600;
            width: 100%;
          }

          .ant-modal-close {
            top: 20px;
            right: 20px;
            width: 32px;
            height: 32px;
          }

          .ant-modal-close-x {
            width: 32px;
            height: 32px;
            line-height: 32px;
            border-radius: 8px;
            background: #f8fafc;
            color: #64748b;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .ant-modal-close-x:hover {
            background: #e2e8f0;
            color: #475569;
          }

          /* Custom scrollbar for messages */
          .flex-1.overflow-y-auto::-webkit-scrollbar {
            width: 6px;
          }

          .flex-1.overflow-y-auto::-webkit-scrollbar-track {
            background: transparent;
          }

          .flex-1.overflow-y-auto::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 3px;
          }

          .flex-1.overflow-y-auto::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }

          /* Message animations */
          .message-group {
            animation: fadeInUp 0.3s ease-out;
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Smooth scrolling */
          .flex-1.overflow-y-auto {
            scroll-behavior: smooth;
          }
        `}</style>
      </Modal>

      {/* ================= Loading Modal ================= */}
      <Modal
        title="Create Loading"
        open={loadingModalVisible}
        onCancel={() => setLoadingModalVisible(false)}
        okText="Create Loading"
        onOk={async () => {
          try {
            await createLoading({
              orderIds: loadingModalTargetIds,
              comment: loadingModalComment,
            });
          } catch {
            /* handled already */
          }
        }}
        confirmLoading={loadingCreating}
        width={560}
        destroyOnClose
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-700">
            You are creating a loading for <strong>{loadingModalTargetIds.length}</strong> order(s).
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">
              Loading name / comment (optional)
            </label>
            <TextArea
              value={loadingModalComment}
              onChange={(e) => setLoadingModalComment(e.target.value)}
              placeholder="Example: Loading 1 – Dublin Delivery"
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Orders in this loading</label>
            <div className="max-h-40 overflow-auto border rounded p-2 bg-gray-50">
              {loadingModalTargetIds.map((id) => {
                const rec = orders.find((o) => o.id === id);
                return (
                  <div key={id} className="text-xs text-gray-700 py-1 border-b last:border-b-0">
                    <span className="font-mono mr-2">#{id}</span>
                    {rec?.product_name || "No product"}
                    <span className="ml-2 text-gray-400">({formatDate(rec?.orderDate)})</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>
      {/* ================= END Loading Modal ================= */}

      {/* ================= Loading DETAILS Modal ================= */}
      <Modal
        title={`Loading details (${loadingDetailsList.length})`}
        open={loadingDetailsVisible}
        onCancel={() => {
          setLoadingDetailsVisible(false);
          setLoadingDetailsList([]);
        }}
        footer={null}
        width={640}
        destroyOnClose
      >
        <div className="space-y-3">
          {loadingDetailsList.length === 0 ? (
            <div className="text-sm text-gray-500">No loadings found</div>
          ) : (
            loadingDetailsList.map((l, idx) => {
              const loaderIndex = l.loader_index ?? idx + 1;
              return (
                <div
                  key={l.id || idx}
                  className="flex items-start justify-between gap-3 p-3 border rounded bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="font-semibold">Loading {loaderIndex}</div>
                    <div className="text-xs text-gray-500">{formatDate(l.createdAt)}</div>
                    {l.comment && <div className="text-sm text-gray-700 mt-1">{l.comment}</div>}
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      size="small"
                      onClick={() => {
                        // open individual loading edit/view could be added here
                        // For now we just show details in the modal list
                        // No-op
                      }}
                    >
                      Details
                    </Button>

                    <Button
                      danger
                      size="small"
                      onClick={() => {
                        // call remove (it has confirm inside)
                        removeProductionLoading(l.id);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Modal>
      {/* ================= END Loading DETAILS Modal ================= */}

      <style jsx>{`
        .modern-orders-table .ant-table-thead > tr > th {
          background: #f8fafc;
          color: #374151;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
          padding: 12px 16px;
        }
        .modern-orders-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 12px 16px;
          transition: all 0.2s ease;
        }
        .modern-orders-table .ant-select-selector {
          border-radius: 8px !important;
        }
        .modern-orders-table .ant-input,
        .modern-orders-table .ant-input-textarea {
          border-radius: 8px !important;
        }
/* ===== FINAL COMPACT LOADING CARD (SHORTER HEIGHT) ===== */

.loading-card-final {
  position: relative;
  min-width: 110px;
  max-width: 155px;
  padding: 8px 10px;          /* ⬇ reduced from 10px 12px */
  background: #ffffff;
  border-radius: 8px;         /* ⬇ slightly tighter */
  border: 1px solid #e5e7eb;
  border-left-width: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;                   /* ⬇ reduced spacing */
}

/* Title */
.loading-card-title {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  line-height: 1.1;           /* ⬇ tighter */
}

/* Date */
.loading-card-date {
  font-size: 11px;            /* ⬇ from 12px */
  color: #6b7280;
  line-height: 1.1;
}

/* View (3rd row) */
.loading-card-view {
  margin-top: 2px;            /* ⬇ reduced */
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  font-size: 11px;            /* ⬇ from 12px */
  color: #2563eb;
  text-decoration: underline;
  cursor: pointer;
  line-height: 1.1;
}

/* +N badge */
.loading-card-more {
  position: absolute;
  top: 6px;                   /* ⬇ tighter */
  right: 6px;
  font-size: 10px;            /* ⬇ smaller */
  font-weight: 600;
  color: #1f2937;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 1px 5px;           /* ⬇ reduced */
}

/* Pending */
.loading-card-empty {
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
}


      `}</style>
    </Spin>
  );
};

export default ProductionOrders;
